import { endOfDay, formatISO, sub } from 'date-fns';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';

import { type AssetSearchFilters } from '@air/api/types';

import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { usePrivateWorkspaceAgentPromptCardClipsSearch } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPromptCardClipsSearch';
import { toWorkspaceAgentPromptCardThumbnails } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentPromptCardThumbnails';

/** Not on a board. Paired with the stale-on-a-board search because `Clips.search` ANDs filters. */
const UNUSED_ASSETS_NOT_ON_BOARDS_FILTERS: AssetSearchFilters = {
  isOnBoards: { is: false },
};

export const usePrivateWorkspaceAgentUnusedAssetsPromptCard = () => {
  const { workspaceId } = usePrivateWorkspaceAgentPanelSession();
  const unusedAssetsLookback = sub(new Date(), { months: 12 });
  const staleOnBoardsUpdatedEnd = formatISO(endOfDay(unusedAssetsLookback), { representation: 'complete' });
  const staleOnBoardsFilters = useMemo(
    (): AssetSearchFilters => ({
      isOnBoards: { is: true },
      updated: {
        end: staleOnBoardsUpdatedEnd,
      },
    }),
    [staleOnBoardsUpdatedEnd],
  );

  const notOnBoards = usePrivateWorkspaceAgentPromptCardClipsSearch({
    filters: UNUSED_ASSETS_NOT_ON_BOARDS_FILTERS,
    workspaceId,
  });
  const staleOnBoards = usePrivateWorkspaceAgentPromptCardClipsSearch({
    filters: staleOnBoardsFilters,
    workspaceId,
  });
  const clips = useMemo(
    () => uniqBy([...(notOnBoards.data?.data.clips ?? []), ...(staleOnBoards.data?.data.clips ?? [])], 'id'),
    [notOnBoards.data?.data.clips, staleOnBoards.data?.data.clips],
  );
  const thumbnails = useMemo(() => toWorkspaceAgentPromptCardThumbnails(clips), [clips]);
  const total = (notOnBoards.data?.data.total ?? 0) + (staleOnBoards.data?.data.total ?? 0);

  return {
    isPending: notOnBoards.isLoading || staleOnBoards.isLoading,
    staleSinceYear: unusedAssetsLookback.getFullYear(),
    thumbnails,
    total,
  };
};
